import {
  CheckCircleOutlined,
  CheckOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  FileExcelOutlined,
  FilterOutlined,
  MoreOutlined,
  SearchOutlined,
  TransactionOutlined,
} from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Menu,
  Checkbox,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useRef, useEffect, useMemo } from 'react';

import { WithdrawalExportService } from '../../utils/excelExport';
import PayCustomerModal from '../financial/PayCustomerModal';
import { formatCardNumber, formatCurrency } from '../withdrawal-check/utils';

dayjs.extend(utc);

interface WithdrawalTransaction {
  id: number;
  transactionDate: string;
  createdAt?: string;
  amount: number;
  feeForCustomer?: number;
  customerType?: '%' | 'vn'; // Loại phí khách hàng: % hoặc VND
  feeApplied?: number;
  commissionRateForPartner?: number; // Phí CTV (%)
  balance: number;
  status: string;
  returnToCompany: boolean;
  employee: {
    id: number;
    name: string;
    contactInfo?: string;
  };
  posDevice?: {
    id: number;
    deviceCode: string;
    location: string;
  };
  creditCard?: {
    id: number;
    cardNumber: string;
    cardHolderName?: string;
    customer?: { name?: string };
  };
  paymentInfo?: {
    totalPaid: number;
    paymentCount: number;
    remainingAmount: number;
    totalAmount: number;
  };
}

interface WithdrawalServiceTableProps {
  transactions: WithdrawalTransaction[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  onTableChange: (pagination: any) => void;
  onToggleStatus: (transactionId: number) => void;
  onPayment: (transaction: WithdrawalTransaction) => void;
  onDelete: (transactionId: number) => void;
  onEdit?: (transaction: WithdrawalTransaction) => void;
  onFilter?: (filters: any) => void;
  onFilterChange: (filters: Record<string, any>) => void;
  onPayCustomer?: (transaction: WithdrawalTransaction) => void;
  isDeletedView?: boolean; // Flag để biết đang xem deleted transactions
  isAdmin?: boolean; // Chỉ admin mới thấy nút "Nhận tiền từ POS"
}

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const WithdrawalServiceTable: React.FC<WithdrawalServiceTableProps> = ({
  transactions,
  loading,
  pagination,
  onTableChange,
  onToggleStatus,
  onPayment,
  onDelete,
  onEdit,
  onFilter,
  onFilterChange,
  onPayCustomer,
  isDeletedView = false,
  isAdmin = false,
}) => {
  const [selectedTransactionForPay, setSelectedTransactionForPay] =
    React.useState<WithdrawalTransaction | null>(null);
  const [payCustomerModalVisible, setPayCustomerModalVisible] =
    React.useState(false);
  const filtersRef = useRef<Record<string, any>>({});
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [sorter, setSorter] = React.useState<any>({});

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = React.useState(1500);

  // Tạo danh sách POS options từ transactions
  const posOptions = useMemo(() => {
    const posMap = new Map<
      string,
      { id: number; deviceCode: string; location: string }
    >();

    transactions.forEach((transaction) => {
      if (transaction.posDevice) {
        const key = `${transaction.posDevice.id}`;
        if (!posMap.has(key)) {
          posMap.set(key, transaction.posDevice);
        }
      }
    });

    return Array.from(posMap.values()).map((pos) => ({
      label: `${pos.deviceCode}`,
      value: pos.id.toString(),
      deviceCode: pos.deviceCode,
      location: pos.location,
    }));
  }, [transactions]);

  // Đồng bộ scroll từ Table lên thanh trên
  const handleTableScroll = () => {
    const tableBody = tableWrapperRef.current?.querySelector(
      '.ant-table-body'
    ) as HTMLDivElement;
    if (
      tableBody &&
      topScrollRef.current &&
      topScrollRef.current.scrollLeft !== tableBody.scrollLeft
    ) {
      topScrollRef.current.scrollLeft = tableBody.scrollLeft;
    }
  };

  // Gắn sự kiện scroll cho Table body
  useEffect(() => {
    const tableBody = tableWrapperRef.current?.querySelector(
      '.ant-table-body'
    ) as HTMLDivElement;
    if (tableBody) {
      tableBody.addEventListener('scroll', handleTableScroll);
      // Cập nhật chiều rộng thanh scroll ảo theo bảng thực tế
      setScrollWidth(tableBody.scrollWidth);
      return () => {
        tableBody.removeEventListener('scroll', handleTableScroll);
      };
    }
  }, [transactions]);

  // Khi bảng resize, cập nhật lại chiều rộng thanh scroll ảo
  useEffect(() => {
    const updateWidth = () => {
      const tableBody = tableWrapperRef.current?.querySelector(
        '.ant-table-body'
      ) as HTMLDivElement;
      if (tableBody) setScrollWidth(tableBody.scrollWidth);
    };
    window.addEventListener('resize', updateWidth);
    updateWidth();
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleChange = (pagination: any, filtersArg: any, sorterArg: any) => {
    filtersRef.current = filtersArg;
    setFilters(filtersArg);
    setSorter(sorterArg);

    // Gọi callback onFilterChange để parent component xử lý filtering
    onFilterChange(filtersArg);

    onTableChange(pagination);
  };

  const handleClearFilters = () => {
    const clearedFilters = {};
    setFilters(clearedFilters);
    setSorter({});
    filtersRef.current = clearedFilters;

    // Gọi callback onFilterChange với filters đã clear
    onFilterChange(clearedFilters);

    onTableChange({ ...pagination, current: 1 }); // reset về trang 1
  };

  const handleExportExcel = async (transaction: WithdrawalTransaction) => {
    try {
      // Convert WithdrawalTransaction to TransactionResponse format (customer đủ trường cho CreditCardSimpleDto)
      const transactionResponse = {
        ...transaction,
        transactionType: 'withdrawal' as const,
        transactionFor: 'withdrawal' as const,
        creditCard: transaction.creditCard
          ? {
              ...transaction.creditCard,
              cardHolderName: transaction.creditCard.cardHolderName || '',
              customer: transaction.creditCard.customer
                ? {
                    id: 0,
                    name: transaction.creditCard.customer.name ?? '',
                    contactInfo: '',
                    email: '',
                  }
                : undefined,
            }
          : undefined,
      };
      await WithdrawalExportService.exportToExcel(transactionResponse);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const handlePayCustomer = (transaction: WithdrawalTransaction) => {
    setSelectedTransactionForPay(transaction);
    setPayCustomerModalVisible(true);
  };

  const handlePayCustomerModalClose = () => {
    setPayCustomerModalVisible(false);
    setSelectedTransactionForPay(null);
  };

  const handlePayCustomerSuccess = () => {
    handlePayCustomerModalClose();
    onPayCustomer?.(selectedTransactionForPay!);
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Hoàn thành
          </Tag>
        );
      case 'PENDING':
        return (
          <Tag icon={<ClockCircleOutlined />} color="warning">
            Đang chờ
          </Tag>
        );
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const getColumns = () => {
    const columns: ColumnsType<WithdrawalTransaction> = [
      {
        title: 'Ngày giao dịch',
        dataIndex: 'transactionDate',
        key: 'transactionDate',
        width: 140,
        render: (date: string, record: any) => (
          <div>
            <div className="font-medium">
              {dayjs(date).format('DD-MM-YYYY')}
            </div>
            <div className="text-xs text-gray-500">
              {record.createdAt
                ? dayjs(record.createdAt).format('HH:mm')
                : '--:--'}
            </div>
          </div>
        ),
        sorter: (a, b) =>
          new Date(a.transactionDate).getTime() -
          new Date(b.transactionDate).getTime(),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }) => (
          <div style={{ padding: 8 }}>
            <RangePicker
              value={selectedKeys[0] as any}
              onChange={(dates) => setSelectedKeys(dates ? [dates as any] : [])}
              style={{ width: 300, marginBottom: 8, display: 'block' }}
              format="DD/MM/YYYY"
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Lọc
              </Button>
              <Button
                onClick={() => clearFilters?.()}
                size="small"
                style={{ width: 90 }}
              >
                Xóa
              </Button>
            </Space>
          </div>
        ),
        filterIcon: (filtered: boolean) => (
          <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value: any, record) => {
          if (!value || !value[0] || !value[1]) return true;
          const recordDate = new Date(record.transactionDate);
          const startDate = value[0].toDate();
          const endDate = value[1].toDate();
          return recordDate >= startDate && recordDate <= endDate;
        },
        filteredValue: filters['transactionDate'] || null,
        sortOrder:
          filtersRef.current.columnKey === 'transactionDate' &&
          filtersRef.current.order,
      },
      {
        title: 'Thẻ tín dụng',
        key: 'creditCard',
        width: 150,
        render: (_, record) => (
          <div>
            <div className="font-medium">
              {record.creditCard
                ? formatCardNumber(record.creditCard.cardNumber)
                : 'N/A'}
            </div>
            <div className="text-xs text-gray-500">
              {record.creditCard?.cardHolderName || 'Chưa có tên'}
            </div>
          </div>
        ),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }) => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm thẻ/tên chủ thẻ"
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => confirm()}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Tìm
              </Button>
              <Button
                onClick={() => clearFilters?.()}
                size="small"
                style={{ width: 90 }}
              >
                Xóa
              </Button>
            </Space>
          </div>
        ),
        filterIcon: (filtered: boolean) => (
          <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) => {
          const searchText = (value as string).toLowerCase();
          return (
            record.creditCard?.cardNumber?.includes(searchText) ||
            record.creditCard?.cardHolderName
              ?.toLowerCase()
              .includes(searchText) ||
            false
          );
        },
        filteredValue: filters['creditCard'] || null,
      },
      {
        title: 'Số tiền',
        dataIndex: 'amount',
        key: 'amount',
        width: 110,
        align: 'right',
        render: (amount: number) => (
          <span className="font-semibold text-blue-600">
            {formatCurrency(amount)}
          </span>
        ),
        sorter: (a, b) => a.amount - b.amount,
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }) => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm số tiền"
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => confirm()}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Tìm
              </Button>
              <Button
                onClick={() => clearFilters?.()}
                size="small"
                style={{ width: 90 }}
              >
                Xóa
              </Button>
            </Space>
          </div>
        ),
        filterIcon: (filtered: boolean) => (
          <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value: any, record) => {
          if (!value) return true;
          const amount = record.amount;
          return amount.toString().includes(value as string);
        },
        filteredValue: filters['amount'] || null,
      },
      {
        title: (
          <Tooltip title="Lọc theo loại phí khách hàng">
            <span>Phí KH</span>
          </Tooltip>
        ),
        key: 'feeForCustomer',
        width: 110,
        align: 'right',
        render: (_, record) => {
          const { feeForCustomer, customerType } = record;

          if (!feeForCustomer || feeForCustomer === 0) {
            return <span className="text-gray-400">0</span>;
          }

          if (customerType === 'vn') {
            return (
              <span className="text-blue-500 font-medium">
                {formatCurrency(feeForCustomer)}
              </span>
            );
          } else {
            return (
              <span className="text-orange-500 font-medium">
                {feeForCustomer.toFixed(2)}%
              </span>
            );
          }
        },
        filterIcon: (filtered: boolean) => (
          <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        filters: [
          { text: 'VND', value: 'vn' },
          { text: 'Phần trăm (%)', value: '%' },
          { text: 'Không có phí', value: 'none' },
        ],
        onFilter: (value, record) => {
          const { feeForCustomer, customerType } = record;

          if (value === 'vn') {
            return Boolean(
              customerType === 'vn' && feeForCustomer && feeForCustomer > 0
            );
          } else if (value === '%') {
            return Boolean(
              customerType === '%' && feeForCustomer && feeForCustomer > 0
            );
          } else if (value === 'none') {
            return Boolean(!feeForCustomer || feeForCustomer === 0);
          }

          return true;
        },
        filteredValue: filters['feeForCustomer'] || null,
      },
      {
        title: (
          <Tooltip title="Lọc theo thiết bị POS - có thể chọn nhiều">
            <span>POS</span>
          </Tooltip>
        ),
        key: 'posDevice',
        width: 120,
        render: (_, record) =>
          record.posDevice ? (
            <div>
              <div className="font-medium">{record.posDevice.deviceCode}</div>
              <div className="text-xs text-gray-500">
                {record.posDevice.location}
              </div>
              <div className="text-xs text-orange-500 font-medium">
                {record.feeApplied ? `${record.feeApplied}%` : 'Không phí'}
              </div>
            </div>
          ) : (
            <span className="text-gray-400">N/A</span>
          ),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }) => (
          <div style={{ padding: 8 }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Chọn POS:</Text>
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              <Checkbox.Group
                options={posOptions}
                value={selectedKeys}
                onChange={(checkedValues) => setSelectedKeys(checkedValues)}
                style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
              />
            </div>
            <Space style={{ marginTop: 8 }}>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Lọc
              </Button>
              <Button
                onClick={() => clearFilters?.()}
                size="small"
                style={{ width: 90 }}
              >
                Xóa
              </Button>
            </Space>
          </div>
        ),
        filterIcon: (filtered: boolean) => (
          <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) => {
          if (!record.posDevice) return false;
          const selectedValues = Array.isArray(value) ? value : [value];
          return selectedValues.includes(record.posDevice.id.toString());
        },
        filteredValue: filters['posDevice'] || null,
      },

      {
        title: 'Sau phí',
        key: 'afterFee',
        width: 110,
        align: 'right',
        render: (_, record) => {
          const { amount, feeForCustomer, customerType } = record;
          let afterFee = amount;
          if (feeForCustomer && customerType === '%') {
            afterFee = amount - amount * (feeForCustomer / 100);
          }
          return (
            <span className="font-semibold text-green-600">
              {formatCurrency(afterFee)}
            </span>
          );
        },
        sorter: (a, b) => {
          const getAfterFee = (record: WithdrawalTransaction) => {
            const { amount, feeForCustomer, customerType } = record;
            if (feeForCustomer && customerType === '%') {
              return amount - amount * (feeForCustomer / 100);
            }
            return amount;
          };
          return getAfterFee(a) - getAfterFee(b);
        },
      },
      {
        title: 'Thanh toán',
        key: 'payment',
        width: 120,
        align: 'center',
        render: (_, record) => {
          const paymentInfo = record.paymentInfo;

          if (!paymentInfo) {
            return <div className="text-xs text-gray-400">Chưa có</div>;
          }

          const { totalPaid, paymentCount, remainingAmount, totalAmount } =
            paymentInfo;

          return (
            <div className="text-xs">
              <div className="text-green-600 font-medium">
                {formatCurrency(totalPaid)}
              </div>
              <div className="text-gray-500">{paymentCount} lần</div>
              {remainingAmount > 0 && (
                <div className="text-orange-500 text-xs">
                  Còn: {formatCurrency(remainingAmount)}
                </div>
              )}
              {remainingAmount < 0 && (
                <div className="text-blue-500 text-xs">
                  Thừa: {formatCurrency(Math.abs(remainingAmount))}
                </div>
              )}
            </div>
          );
        },
        filterIcon: (filtered: boolean) => (
          <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        filters: [
          { text: 'Đã thanh toán', value: 'paid' },
          { text: 'Chưa thanh toán', value: 'unpaid' },
        ],
        onFilter: (value, record) => {
          const paymentInfo = record.paymentInfo;
          const hasPaidPayment = paymentInfo && paymentInfo.paymentCount > 0;

          if (value === 'paid') {
            return Boolean(hasPaidPayment);
          } else if (value === 'unpaid') {
            return Boolean(!hasPaidPayment);
          }
          return true;
        },
        filteredValue: filters['payment'] || null,
      },
      {
        title: 'Phí CTV (%)',
        dataIndex: 'commissionRateForPartner',
        key: 'commissionRateForPartner',
        width: 100,
        align: 'right',
        render: (commission?: number) => (
          <span className="text-purple-500">
            {commission ? `${commission}%` : 'N/A'}
          </span>
        ),
        sorter: (a, b) =>
          (a.commissionRateForPartner || 0) - (b.commissionRateForPartner || 0),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }) => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm phí CTV"
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => confirm()}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Tìm
              </Button>
              <Button
                onClick={() => clearFilters?.()}
                size="small"
                style={{ width: 90 }}
              >
                Xóa
              </Button>
            </Space>
          </div>
        ),
        filterIcon: (filtered: boolean) => (
          <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) =>
          record.commissionRateForPartner
            ?.toString()
            .includes(value as string) || false,
        filteredValue: filters['commissionRateForPartner'] || null,
      },
      {
        title: 'Nhân viên',
        key: 'employee',
        width: 140,
        render: (_, record) => (
          <div>
            <div className="font-medium">{record.employee.name}</div>
            {record.employee.contactInfo && (
              <div className="text-xs text-gray-500">
                {record.employee.contactInfo}
              </div>
            )}
          </div>
        ),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }) => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm nhân viên"
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => confirm()}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Tìm
              </Button>
              <Button
                onClick={() => clearFilters?.()}
                size="small"
                style={{ width: 90 }}
              >
                Xóa
              </Button>
            </Space>
          </div>
        ),
        filterIcon: (filtered: boolean) => (
          <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) =>
          record.employee.name
            .toLowerCase()
            .includes((value as string).toLowerCase()),
        filteredValue: filters['employee'] || null,
      },

      {
        title: 'Hành động',
        key: 'actions',
        width: 160,
        align: 'center',
        fixed: 'right',
        render: (_, record) => (
          <Space size="small">
            <Tooltip title="Ghi nhận thanh toán">
              <Button
                type="text"
                icon={<DollarOutlined />}
                onClick={() => onPayment(record)}
                size="small"
                className="text-blue-500 hover:bg-blue-50"
              />
            </Tooltip>
            <Tooltip title="Trả cho khách">
              <Button
                type="text"
                icon={<TransactionOutlined />}
                onClick={() => handlePayCustomer(record)}
                size="small"
                className="text-orange-500 hover:bg-orange-50"
              />
            </Tooltip>
            <Tooltip title="Xuất hóa đơn Excel">
              <Button
                type="text"
                icon={<FileExcelOutlined />}
                onClick={() => handleExportExcel(record)}
                size="small"
                className="text-green-500 hover:bg-green-50"
              />
            </Tooltip>
            {isAdmin && (
              <Tooltip
                title={
                  record.status === 'COMPLETED'
                    ? 'Chưa nhận tiền từ POS'
                    : 'Nhận tiền từ POS'
                }
              >
                <Button
                  type="default"
                  icon={
                    record.status === 'COMPLETED' ? (
                      <CheckOutlined />
                    ) : (
                      <span></span>
                    )
                  }
                  onClick={() => onToggleStatus(record.id)}
                  size="small"
                  className={
                    record.status === 'COMPLETED'
                      ? 'text-green-500 hover:bg-green-50'
                      : 'text-gray-400 hover:bg-gray-50'
                  }
                />
              </Tooltip>
            )}
            <Dropdown
              menu={{
                items: [
                  ...(onEdit
                    ? [
                        {
                          key: 'edit',
                          icon: <EditOutlined />,
                          label: 'Sửa giao dịch',
                          onClick: () => onEdit(record),
                        },
                      ]
                    : []),
                  {
                    key: 'delete',
                    icon: <DeleteOutlined />,
                    label: 'Xóa giao dịch',
                    danger: true,
                    onClick: () => {
                      if (
                        window.confirm(
                          'Bạn có chắc chắn muốn xóa giao dịch này không?'
                        )
                      ) {
                        onDelete(record.id);
                      }
                    },
                  },
                ],
              }}
              trigger={['click']}
            >
              <Tooltip title="Thêm thao tác">
                <Button
                  type="text"
                  icon={<MoreOutlined />}
                  size="small"
                  className="text-gray-500 hover:bg-gray-50"
                />
              </Tooltip>
            </Dropdown>
          </Space>
        ),
        filteredValue: filters['actions'] || null,
      },
    ];

    return columns;
  };

  return (
    <div>
      {/* Filter Controls */}
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Text strong>Bộ lọc:</Text>
          {Object.keys(filters).length > 0 && (
            <Button
              type="default"
              icon={<ClearOutlined />}
              size="small"
              onClick={handleClearFilters}
              className="text-red-500 hover:text-red-700"
            >
              Xóa tất cả bộ lọc
            </Button>
          )}
        </div>
        <div className="text-sm text-gray-500">
          {Object.keys(filters).length > 0 ? (
            <span>
              Đang áp dụng {Object.keys(filters).length} bộ lọc - Hiển thị{' '}
              {pagination.total} trong tổng số giao dịch
            </span>
          ) : (
            <span>Hiển thị tất cả {pagination.total} giao dịch</span>
          )}
        </div>
      </div>

      {/* Table */}
      <Table<WithdrawalTransaction>
        columns={getColumns()}
        dataSource={transactions}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} trong ${total} giao dịch`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleChange}
        scroll={{ x: 1500 }}
        size="middle"
        className="w-full"
      />

      {/* Pay Customer Modal */}
      {selectedTransactionForPay && (
        <PayCustomerModal
          visible={payCustomerModalVisible}
          onCancel={handlePayCustomerModalClose}
          onSuccess={handlePayCustomerSuccess}
          transaction={{
            id: selectedTransactionForPay.id,
            amount: selectedTransactionForPay.amount,
            balance: selectedTransactionForPay.balance,
            creditCard: selectedTransactionForPay.creditCard,
            transactionDate: selectedTransactionForPay.transactionDate,
            feeForCustomer: selectedTransactionForPay.feeForCustomer,
            customerType: selectedTransactionForPay.customerType,
          }}
        />
      )}
    </div>
  );
};

export default WithdrawalServiceTable;
